import { useEffect } from 'react';
import { API } from '../api/api';
import { combineBootstrapResults } from '../utils/bootstrapEvents';

export function useAuthBootstrap({
  authenticated,
  setAuthenticated,
  setProjects,
  setEvents,
  showError,
  setError,
  setBillableAutoFill,
  setCurrentView,
  setWeeklyHours,
  setIsTimelogsLoaded,
  setCanUseBD,
}) {
  // Bootstrap auth + initial data
  useEffect(() => {
    API.checkAuth()
      .then(async (data) => {
        setBillableAutoFill(!!data.billableAutoFill);
        if (setWeeklyHours && data.weeklyHours) setWeeklyHours(data.weeklyHours);
        if (setCanUseBD) setCanUseBD(!!data.canUseBD);

        if (!data.authenticated) {
          const isMobile = /Mobi|Android/i.test(navigator.userAgent);
          if (isMobile) {
            const { url } = await API.loginPortal();
            window.location.href = url;
            return;
          }
          const { url } = await API.loginPortal();
          const popup = window.open(url, 'oauthPopup', 'width=600,height=700');
          window.addEventListener('message', function handleOAuthMsg(event) {
            if (event.data && event.data.type === 'oauth-success') {
              window.location.reload();
              window.removeEventListener('message', handleOAuthMsg);
            }
          });
          return;
        }

        setAuthenticated(true);

        // Fetch user's last calendar view preference
        API.getUserViewPreference().then((viewData) => {
          if (viewData.view && setCurrentView) {
            setCurrentView(viewData.view);
          }
        }).catch(() => {});

        // Fetch projects
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});

        // Fetch timelogs (projects + people)
        const projectsReq = API.fetchProjectsTimelogs();
        const peopleReq = API.fetchPeopleTimelogs();

        // Settled, not all: a failed Projects load must not hide the People logs.
        Promise.allSettled([projectsReq, peopleReq])
          .then(([projectsResult, peopleResult]) => {
            const peopleLogToEvent = (log) => {
              const date = new Date(log.workDate);

              let start, end;
              if (log.fromTimeInTimeFormat && log.toTimeInTimeFormat) {
                // Use explicit from/to times
                start = new Date(date);
                const [fromHour, fromMin] = log.fromTimeInTimeFormat.split(':').map(Number);
                start.setHours(fromHour, fromMin, 0, 0);

                end = new Date(date);
                const [toHour, toMin] = log.toTimeInTimeFormat.split(':').map(Number);
                end.setHours(toHour, toMin, 0, 0);
              } else {
                // Fallback: start at 08:00, use hoursInMins
                start = new Date(date);
                start.setHours(4, 0, 0, 0);
                end = new Date(start.getTime() + (log.hoursInMins ? log.hoursInMins : 60) * 60000);
              }

              return {
                title: log.description || log.jobName || '',
                start,
                end,
                projectId: '',
                timecodeId: log.jobId || '',
                projectName: '',
                timecodeName: log.jobName || '',
                approval_status: log.approvalStatus || '',
                billingtype: log.billingStatus === 'billable' ? 'Billable' : 'Non Billable',
                logId: `people:${log.timelogId}`,
                isPeople: true,
              };
            };

            const { events, failed } = combineBootstrapResults(projectsResult, peopleResult, peopleLogToEvent);
            setEvents(events);
            if (failed) showError('Data Error', 'Failed to fetch timelogs. Please try refreshing the page.');
            if (setIsTimelogsLoaded) setIsTimelogsLoaded(true);
          })
          .catch(() => {
            showError('Data Error', 'Failed to fetch timelogs. Please try refreshing the page.');
            if (setIsTimelogsLoaded) setIsTimelogsLoaded(true);
          });
      })
      .catch(() => {
        setError('Error 3 - contact ZRM');
      });
  }, [showError]);

  // 30s ping keepalive
  useEffect(() => {
      if (!authenticated) return;
      const interval = setInterval(() => {
        API.ping().catch(() => {
          window.location.reload();
        });
      }, 30000);
      return () => clearInterval(interval);
    }, [authenticated]);
}